import { HStack, Text, VStack } from '@chakra-ui/react';
import { CockpitEditorLayout } from '@components/cockpit/layouts/CockpitEditorLayout';
import { useSensorInstallationContext } from '@components/cockpit/panels/sensorInstallation/SensorInstallationContext';
import { appIntl } from '@components/locale/IntlGlobalProvider';
import { Checkbox } from '@components/ui/checkbox/Checkbox';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasSensorRoleFromActiveProfile, selectActiveSite } from '@redux/authent/authent.selectors';
import { selectAppCodes } from '@redux/sensors/sensors.selectors';
import { useCreateSensorConfigurationMutation, useUpdateSensorConfigurationMutation } from '@services/c2/c2.api';
import { createErrorMessage } from '@utils/common.utils';
import { sensorFamilyFromSensorType } from '@utils/sensors/configuration.constants';
import { emptySensorByType } from '@utils/sensors/sensors.constants';
import { FormikProvider, useFormik } from 'formik';
import { isEqual } from 'lodash';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { ActionEnum, Mark, MarkEnum } from '@/types/commons/commons.types';
import { SensorConfiguration } from '@/types/sensor/configuration.types';
import { EditableType, GetSensorOrSubSensorConfigurationWithShorthand } from '@/types/sensor/sensorInstallation.types';

import { Breadcrumb } from '../../Breadcrumb';
import SensorTrigram from '../../monitoring/detail/SensorTrigram';
import SensorEditingProvider, { useSensorEditingContext } from './SensorEditingContext';
import SensorEditingForm from './SensorEditingForm';
import SensorExpertForm from './SensorExpertForm';
import SensorFormSteps from './SensorFormSteps';

type Props<T extends EditableType> = {
  type: T;
  editedSensor: GetSensorOrSubSensorConfigurationWithShorthand<T>;
};

type FormValues<T extends EditableType> = Partial<GetSensorOrSubSensorConfigurationWithShorthand<T>>;

function SensorEditingPanelContent<T extends EditableType>({ type, editedSensor }: Readonly<Props<T>>) {
  const { formatMessage } = appIntl();
  const { isNewSensor, onCancel } = useSensorInstallationContext();
  const { steps, validationSchema } = useSensorEditingContext();
  const appCodes = useAppSelector(selectAppCodes);
  const site = useAppSelector(selectActiveSite);
  const [createConfiguration, { isLoading: isLoadingCreate }] = useCreateSensorConfigurationMutation();
  const [updateConfiguration, { isLoading: isLoadingUpdate }] = useUpdateSensorConfigurationMutation();
  const sensorAction = {
    action: ActionEnum.MAINTAIN,
    sensorFamily: sensorFamilyFromSensorType[type],
  };
  const canSeeMonitoring = useAppSelector((state) => hasSensorRoleFromActiveProfile(state, sensorAction));

  const [expertMode, setExpertMode] = useState(steps.length === 0);
  const [sensorText, setSensorText] = useState<string>(JSON.stringify(editedSensor, null, '\t'));
  const [isErrorParsing, setIsErrorParsing] = useState<boolean>(false);
  const emptySensor = emptySensorByType[type];

  const isLoading = isLoadingCreate || isLoadingUpdate;

  const handleCancel = (sensor: SensorConfiguration) => {
    onCancel(canSeeMonitoring ? { type: sensor.type, uniqueKey: sensor.uniqueKey } : null);
  };

  const expertModeSave = () => {
    if (!sensorText) {
      createToast(formatMessage({ id: 'cockpit.sensorInstallation.emptyJson' }), ToastStatusEnum.ERROR);
    }
    try {
      const updatedSensor: SensorConfiguration = { ...emptySensor, ...JSON.parse(sensorText) };
      if (isNewSensor) {
        createConfiguration({
          site: updatedSensor.site,
          sensorConfiguration: updatedSensor,
        })
          .unwrap()
          .then(handleCancel)
          .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
      } else {
        updateConfiguration({
          id: updatedSensor.id,
          site: updatedSensor.site,
          sensor: updatedSensor,
        })
          .unwrap()
          .then(() => onCancel(null))
          .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
      }
    } catch (e) {
      console.error(e);
      if (expertMode) {
        setIsErrorParsing(true);
      }
    }
  };

  const normalModeSave = (values: FormValues<T>) => {
    const marksWithoutPlatforms = editedSensor.marks.filter((mark) => mark.type !== MarkEnum.PLATFORM) as Mark[];
    const updatedMarks = marksWithoutPlatforms.concat(values.marks ?? []);
    if (isNewSensor) {
      const siteCode = site?.code ?? 'local';
      const newSensor: FormValues<T> = {
        ...values,
        code: uuidv4(),
        site: siteCode,
        appCode: appCodes[0],
        marks: updatedMarks,
      };
      createConfiguration({
        site: siteCode,
        sensorConfiguration: newSensor,
      })
        .unwrap()
        .then(handleCancel)
        .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
    } else {
      updateConfiguration({
        id: values.id!,
        site: values.site!,
        sensor: { ...values, marks: updatedMarks } as FormValues<T>,
      })
        .unwrap()
        .then(() => onCancel(null))
        .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
    }
  };

  const formik = useFormik<FormValues<T>>({
    initialValues: editedSensor as FormValues<T>,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => normalModeSave(values),
  });

  const handleSwitchExpertMode = (values: FormValues<T>, sensorText: string) => {
    if (expertMode && !isErrorParsing) {
      const updatedSensor = JSON.parse(sensorText) as FormValues<T>;
      formik.setValues({ ...emptySensor, ...updatedSensor });
      setExpertMode(false);
    } else {
      setSensorText(JSON.stringify(values, null, '\t'));
      setExpertMode(true);
    }
  };

  const canSave = useCallback(() => {
    if (expertMode) {
      return !isErrorParsing && sensorText !== JSON.stringify(editedSensor, null, '\t');
    } else {
      return formik.isValid && !isEqual(formik.initialValues, formik.values);
    }
  }, [editedSensor, expertMode, formik.initialValues, formik.isValid, formik.values, isErrorParsing, sensorText]);

  return (
    <FormikProvider value={formik}>
      <CockpitEditorLayout
        onBack={() => onCancel(null)}
        hideCancel
        breadcrumbs={
          <Breadcrumb
            items={[
              {
                label: 'Retour',
                action: () => onCancel(null),
              },
            ]}
          />
        }
        actions={
          <Checkbox
            marginRight={4}
            checked={expertMode}
            label={<FormattedMessage id="components.sensorInstallation.form.expertMode" />}
            display={steps.length === 0 ? 'none' : 'flex'}
            disabled={steps.length === 0 || isErrorParsing}
            onChange={() => handleSwitchExpertMode(formik.values, sensorText)}
          />
        }
        onSave={() => (expertMode ? expertModeSave() : formik.submitForm())}
        isSaving={isLoading}
        isSaveDisabled={!canSave()}
      >
        <HStack flex={1} minHeight={0} width="100%" gap={0.25}>
          <VStack
            width="480px"
            height="100%"
            gap={0.25}
            backgroundColor="neutral.900"
            overflow="hidden"
            overflowY="auto"
            className="cockpit-scrollbar-900 no-margin"
          >
            <VStack
              width="100%"
              height="100%"
              backgroundColor="neutral.800"
              gap={5}
              flex={1}
              paddingX={9.5}
              paddingY={13}
              alignItems="start"
              overflow="hidden"
              overflowY="auto"
              className="cockpit-scrollbar-900"
            >
              <HStack gap={2}>
                <SensorTrigram size="L" type={type} />
                <VStack gap={0.5} alignItems="start">
                  <Text
                    paddingY={0.25}
                    paddingX={0.5}
                    backgroundColor="neutral.black"
                    fontWeight="medium"
                    textStyle="md"
                    lineClamp={1}
                    wordBreak="break-all"
                  >
                    {isNewSensor ? (
                      <FormattedMessage id="components.sensorInstallation.form.newSensor" />
                    ) : (
                      formik.initialValues.name
                    )}
                  </Text>
                  <Text textStyle="xl" color="neutral.500" fontFamily="space" fontFeatureSettings="'liga' off">
                    <FormattedMessage id={`sensors.type.${type}`} />
                  </Text>
                </VStack>
              </HStack>
              {!expertMode && <SensorFormSteps />}
            </VStack>
          </VStack>
          {expertMode ? (
            <SensorExpertForm
              sensorText={sensorText}
              setSensorText={setSensorText}
              isErrorParsing={isErrorParsing}
              setIsErrorParsing={setIsErrorParsing}
            />
          ) : (
            <VStack
              height="100%"
              flex={1}
              minHeight={0}
              overflow="hidden"
              overflowY="auto"
              className="cockpit-scrollbar-900"
            >
              <SensorEditingForm<T> />
            </VStack>
          )}
        </HStack>
      </CockpitEditorLayout>
    </FormikProvider>
  );
}

export default function SensorEditingPanel() {
  const { editedSensor, onCancel } = useSensorInstallationContext();

  if (!editedSensor) {
    onCancel(null);
    return null;
  }
  return (
    <SensorEditingProvider type={editedSensor.type}>
      <SensorEditingPanelContent type={editedSensor.type} editedSensor={editedSensor} />
    </SensorEditingProvider>
  );
}
